import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PrintIcon from '@mui/icons-material/Print';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import ManagerApi from '../../api/ManagerApi';
import ProsecutorApi, { ProsecutorDto, FullProsecutorDto } from '../../api/ProsecutorApi';
import {
  COURT_FILING_METHOD_LABELS,
  CourtFilingMethod,
  FullCourtDto,
} from '../../interfaces/CourtInterfaces';
import './ManagerCourtView.scss';
import CourtClientsPanel from '../../components/CourtClientsPanel/CourtClientsPanel';

const ManagerCourtView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const courtGuid = searchParams.get('Court_guid');
  const navigate = useNavigate();
  const [court, setCourt] = useState<FullCourtDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit form state
  const [isEditing, setIsEditing] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    Court_Name: '',
    Court_Email: '',
    Court_Address: '',
    Court_Phone: '',
    Court_Fax: '',
    Court_County: '',
    Court_OffersOffDocket: false,
    Court_OfRecord: false,
    Court_FilingMethod: '' as CourtFilingMethod | '',
    Court_FilingAddress: '',
    Court_FilingNote: '',
    Court_Active: true
  });

  // Prosecutor modal state
  const [prosecutorModalOpen, setProsecutorModalOpen] = useState(false);
  const [selectedProsecutor, setSelectedProsecutor] = useState<any>(null);
  const [prosecutorModalLoading, setProsecutorModalLoading] = useState(false);
  const [prosecutorEditForm, setProsecutorEditForm] = useState({
    Prosecutor_Name: '',
    Prosecutor_Email: '',
    Prosecutor_Phone: '',
    Prosecutor_Disposition: '',
    Prosecutor_Active: true
  });
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  useEffect(() => {
    const fetchCourt = async () => {
      if (!courtGuid) {
        setError('Court ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const courtData = await ManagerApi.getCourt(courtGuid);
        setCourt(courtData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch court details');
        setCourt(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCourt();
  }, [courtGuid]);

  // Populate edit form when court data changes
  useEffect(() => {
    if (court) {
      setEditForm({
        Court_Name: court.Court_Name,
        Court_Email: court.Court_Email,
        Court_Address: court.Court_Address,
        Court_Phone: court.Court_Phone || '',
        Court_Fax: court.Court_Fax || '',
        Court_County: court.Court_County || '',
        Court_FilingMethod: court.Court_FilingMethod ?? '',
        Court_FilingAddress: court.Court_FilingAddress || '',
        Court_FilingNote: court.Court_FilingNote || '',
        Court_OffersOffDocket: !!court.Court_OffersOffDocket,
        Court_OfRecord: !!court.Court_OfRecord,
        Court_Active: court.Court_Active
      });
    }
  }, [court]);

  const handleBack = () => {
    navigate('/courts');
  };

  const handleEditCourt = () => {
    navigate(`/court/edit?Court_guid=${courtGuid}`);
  };

  const handleEditProsecutor = (prosecutorGuid: string) => {
    navigate(`/prosecutor/${prosecutorGuid}/edit`);
  };

  const handleAddProsecutor = () => {
    navigate(`/add-prosecutor?Court_guid=${courtGuid}`);
  };

  const handleEditFormChange = (field: string, value: string | boolean) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setSuccessMessage(null);
    setError(null);
  };

  const handleSaveChanges = async () => {
    if (!courtGuid) return;

    try {
      setActionLoading(true);
      // The dropdown's blank option is an empty string, but "nobody has recorded this" is null on
      // the record. Sending '' would store a method the server does not recognise and the court
      // page would then render a blank where it should say "Not recorded".
      const updatedCourt = await ManagerApi.updateCourt(courtGuid, {
        ...editForm,
        Court_FilingMethod: editForm.Court_FilingMethod || null,
        // "File at" is HIDDEN when the method is email or unset, not cleared, so without this an
        // address typed against a previous method is still submitted and persists invisibly. A
        // court switched from fax to email would keep showing its old fax number to anyone who
        // later switched it back.
        Court_FilingAddress:
          editForm.Court_FilingMethod === '' || editForm.Court_FilingMethod === 'email'
            ? null
            : editForm.Court_FilingAddress.trim() || null,
      });
      setCourt(updatedCourt);
      setIsEditing(false);
      setSuccessMessage('Court updated successfully!');
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update court');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelEdit = () => {
    if (court) {
      setEditForm({
        Court_Name: court.Court_Name,
        Court_Email: court.Court_Email,
        Court_Address: court.Court_Address,
        Court_Phone: court.Court_Phone || '',
        Court_Fax: court.Court_Fax || '',
        Court_County: court.Court_County || '',
        Court_FilingMethod: court.Court_FilingMethod ?? '',
        Court_FilingAddress: court.Court_FilingAddress || '',
        Court_FilingNote: court.Court_FilingNote || '',
        Court_OffersOffDocket: !!court.Court_OffersOffDocket,
        Court_OfRecord: !!court.Court_OfRecord,
        Court_Active: court.Court_Active
      });
    }
    setIsEditing(false);
    setError(null);
  };

  // Prosecutor modal handlers
  const handleOpenProsecutorModal = (prosecutor: any) => {
    setSelectedProsecutor(prosecutor);
    setProsecutorEditForm({
      Prosecutor_Name: prosecutor.Prosecutor_Name,
      Prosecutor_Email: prosecutor.Prosecutor_Email,
      Prosecutor_Phone: prosecutor.Prosecutor_Phone || '',
      Prosecutor_Disposition: prosecutor.Prosecutor_Disposition || '',
      Prosecutor_Active: prosecutor.Prosecutor_Active
    });
    setProsecutorModalOpen(true);
    setShowDeleteConfirmation(false);
  };

  const handleCloseProsecutorModal = () => {
    setProsecutorModalOpen(false);
    setSelectedProsecutor(null);
    setShowDeleteConfirmation(false);
    setError(null);
  };

  const handleProsecutorFormChange = (field: string, value: string | boolean) => {
    setProsecutorEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProsecutor = async () => {
    if (!selectedProsecutor) return;

    try {
      setProsecutorModalLoading(true);
      const updatedProsecutor = await ProsecutorApi.updateProsecutor(
        selectedProsecutor.Prosecutor_guid, 
        prosecutorEditForm
      );

      // Update the court data with the updated prosecutor
      if (court) {
        const updatedProsecutors = court.prosecutors.map(p => 
          p.Prosecutor_guid === selectedProsecutor.Prosecutor_guid 
            ? updatedProsecutor.prosecutor 
            : p
        );
        setCourt({ ...court, prosecutors: updatedProsecutors });
      }

      setSuccessMessage('Prosecutor updated successfully!');
      handleCloseProsecutorModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update prosecutor');
    } finally {
      setProsecutorModalLoading(false);
    }
  };

  const handleDeleteProsecutor = async () => {
    if (!selectedProsecutor) return;

    try {
      setProsecutorModalLoading(true);
      await ProsecutorApi.deleteProsecutor(selectedProsecutor.Prosecutor_guid);

      // Remove the prosecutor from the court data
      if (court) {
        const updatedProsecutors = court.prosecutors.filter(p => 
          p.Prosecutor_guid !== selectedProsecutor.Prosecutor_guid
        );
        setCourt({ ...court, prosecutors: updatedProsecutors });
      }

      setSuccessMessage('Prosecutor removed successfully!');
      handleCloseProsecutorModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove prosecutor');
    } finally {
      setProsecutorModalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="manager-court-view">
        <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manager-court-view">
        <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back to Courts
          </Button>
        </Container>
      </div>
    );
  }

  if (!court) {
    return (
      <div className="manager-court-view">
        <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Court not found
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back to Courts
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="manager-court-view">
      
      <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back to Courts
          </Button>
          
          <Box flexGrow={1}>
            <Typography variant="h4" component="h1" gutterBottom>
              {court.Court_Name}
            </Typography>
          </Box>
        </Box>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            gap: 3,
            mb: 3 
          }}
        >
          {/* Court Details Card */}
          <Box sx={{ flex: 1 }}>
            <Card elevation={3}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    <AccountBalanceIcon />
                  </Avatar>
                  <Typography variant="h6" component="h2">
                    Court Information
                  </Typography>
                  {isEditing ? (
                    <Box ml="auto" display="flex" gap={1}>
                      <IconButton 
                        onClick={handleSaveChanges} 
                        size="small" 
                        color="primary" 
                        disabled={actionLoading}
                        title="Save changes"
                      >
                        <SaveIcon />
                      </IconButton>
                      <IconButton 
                        onClick={handleCancelEdit} 
                        size="small" 
                        color="secondary" 
                        disabled={actionLoading}
                        title="Cancel changes"
                      >
                        <CancelIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box ml="auto" display="flex" gap={1}>
                      <IconButton 
                        onClick={handleStartEdit} 
                        size="small"
                        sx={{ height: 'fit-content' }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                
                <Divider sx={{ mb: 2 }} />
                
                {isEditing ? (
                  // Edit mode form
                  <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                      label="Court Name"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={editForm.Court_Name}
                      onChange={(e) => handleEditFormChange('Court_Name', e.target.value)}
                      disabled={actionLoading}
                    />
                    
                    <TextField
                      label="Court Email"
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="email"
                      value={editForm.Court_Email}
                      onChange={(e) => handleEditFormChange('Court_Email', e.target.value)}
                      disabled={actionLoading}
                    />
                    
                    <TextField
                      label="Court Phone"
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="tel"
                      value={editForm.Court_Phone}
                      onChange={(e) => handleEditFormChange('Court_Phone', e.target.value)}
                      disabled={actionLoading}
                      placeholder="Optional phone number"
                    />

                    <TextField
                      label="Court Fax"
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="tel"
                      value={editForm.Court_Fax}
                      onChange={(e) => handleEditFormChange('Court_Fax', e.target.value)}
                      disabled={actionLoading}
                      placeholder="Optional fax number"
                    />
                    
                    <TextField
                      label="Court Address"
                      variant="outlined"
                      size="small"
                      fullWidth
                      multiline
                      rows={2}
                      value={editForm.Court_Address}
                      onChange={(e) => handleEditFormChange('Court_Address', e.target.value)}
                      disabled={actionLoading}
                    />

                    <TextField
                      label="County"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={editForm.Court_County}
                      onChange={(e) => handleEditFormChange('Court_County', e.target.value)}
                      disabled={actionLoading}
                    />

                    {/*
                      How this court takes a filing (Maria and Hannah, 18 Sep 2026). Until this
                      existed there was nowhere to record it, so staff typed it into the email
                      field, which then sent the letter to an address nobody reads.

                      Blank is a real answer and the default. It means nobody has recorded how this
                      court works, which is different from saying it takes email.
                    */}
                    <TextField
                      select
                      label="Takes filings by"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={editForm.Court_FilingMethod}
                      onChange={(e) => handleEditFormChange('Court_FilingMethod', e.target.value)}
                      disabled={actionLoading}
                      helperText="Leave blank if nobody has checked how this court wants filings"
                    >
                      <MenuItem value="">Not recorded</MenuItem>
                      {(Object.keys(COURT_FILING_METHOD_LABELS) as CourtFilingMethod[]).map((method) => (
                        <MenuItem key={method} value={method}>
                          {COURT_FILING_METHOD_LABELS[method]}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Kept apart from the court's email so a court that files elsewhere still has
                        a working mailbox for everything else. That separation is what Laredo lost
                        when its email was pointed at the internal box. */}
                    {editForm.Court_FilingMethod !== '' && editForm.Court_FilingMethod !== 'email' && (
                      <TextField
                        label="File at"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={editForm.Court_FilingAddress}
                        onChange={(e) => handleEditFormChange('Court_FilingAddress', e.target.value)}
                        disabled={actionLoading}
                        helperText="The website, fax number or postal address to file at"
                      />
                    )}

                    <TextField
                      label="Filing notes"
                      variant="outlined"
                      size="small"
                      fullWidth
                      multiline
                      rows={2}
                      value={editForm.Court_FilingNote}
                      onChange={(e) => handleEditFormChange('Court_FilingNote', e.target.value)}
                      disabled={actionLoading}
                      helperText="Anything else about how this court wants things sent"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={editForm.Court_OffersOffDocket}
                          onChange={(e) => handleEditFormChange('Court_OffersOffDocket', e.target.checked)}
                          disabled={actionLoading}
                        />
                      }
                      label="Offers off docket"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={editForm.Court_OfRecord}
                          onChange={(e) => handleEditFormChange('Court_OfRecord', e.target.checked)}
                          disabled={actionLoading}
                        />
                      }
                      label="Court of record"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={editForm.Court_Active}
                          onChange={(e) => handleEditFormChange('Court_Active', e.target.checked)}
                          disabled={actionLoading}
                        />
                      }
                      label="Active"
                    />
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Court ID
                      </Typography>
                      <Typography variant="body2" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                        {court.Court_guid}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  // View mode
                  <>
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Status
                      </Typography>
                      <Chip
                        label={court.Court_Active ? 'Active' : 'Inactive'}
                        color={court.Court_Active ? 'success' : 'error'}
                        size="small"
                      />
                    </Box>
                    
                    <Box mb={2} display="flex" alignItems="flex-start">
                      <EmailIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body1">
                          {court.Court_Email}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {court.Court_Phone && (
                      <Box mb={2} display="flex" alignItems="flex-start">
                        <PhoneIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography variant="body1">
                            {court.Court_Phone}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {court.Court_Fax && (
                      <Box mb={2} display="flex" alignItems="flex-start">
                        <PrintIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Fax
                          </Typography>
                          <Typography variant="body1">
                            {court.Court_Fax}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    
                    <Box mb={2} display="flex" alignItems="flex-start">
                      <LocationOnIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Address
                        </Typography>
                        <Typography variant="body1">
                          {court.Court_Address}
                        </Typography>
                      </Box>
                    </Box>

                    <Box mb={2}>
                      <Typography variant="subtitle2" color="text.secondary">
                        County
                      </Typography>
                      <Typography variant="body1">
                        {court.Court_County || '—'}
                      </Typography>
                    </Box>

                    {/* How this court takes a filing. Shown whenever it is recorded, because the
                        person about to send a letter is the one who needs it. */}
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Takes filings by
                      </Typography>
                      <Typography variant="body1">
                        {court.Court_FilingMethod
                          ? COURT_FILING_METHOD_LABELS[court.Court_FilingMethod]
                          : 'Not recorded'}
                      </Typography>
                      {court.Court_FilingAddress && (
                        <Typography variant="body2" color="text.secondary">
                          File at {court.Court_FilingAddress}
                        </Typography>
                      )}
                      {court.Court_FilingNote && (
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                          {court.Court_FilingNote}
                        </Typography>
                      )}
                    </Box>

                    <Box mb={2} display="flex" gap={1} flexWrap="wrap">
                      <Chip
                        label={`Offers off docket: ${court.Court_OffersOffDocket == null ? 'Unknown' : court.Court_OffersOffDocket ? 'Yes' : 'No'}`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={`Court of record: ${court.Court_OfRecord == null ? 'Unknown' : court.Court_OfRecord ? 'Yes' : 'No'}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Court ID
                      </Typography>
                      <Typography variant="body2" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                        {court.Court_guid}
                      </Typography>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
          
          {/* Prosecutors Card */}
          <Box sx={{ flex: 1 }}>
            <Card elevation={3} sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ mr: 2, bgcolor: 'secondary.main' }}>
                      <PersonIcon />
                    </Avatar>
                    <Typography variant="h6" component="h2">
                      Prosecutors ({court.prosecutors.length})
                    </Typography>
                  </Box>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddProsecutor}
                  >
                    Add Prosecutor
                  </Button>
                </Box>
                
                <Divider sx={{ mb: 2 }} />
                
                {court.prosecutors.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    No prosecutors assigned to this court.
                  </Typography>
                ) : (
                  <List dense>
                    {court.prosecutors.map((prosecutor, index) => (
                      <React.Fragment key={prosecutor.Prosecutor_guid}>
                        <ListItem
                          sx={{
                            px: 0,
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                              borderRadius: 1,
                            },
                          }}
                          onClick={() => handleOpenProsecutorModal(prosecutor)}
                        >
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="subtitle1">
                                  {prosecutor.Prosecutor_Name}
                                </Typography>
                                <Box display="flex" gap={0.5}>
                                  {prosecutor.Prosecutor_Disposition && (
                                    <Chip
                                      label={prosecutor.Prosecutor_Disposition}
                                      color={prosecutor.Prosecutor_Disposition === 'Friendly' ? 'success' : prosecutor.Prosecutor_Disposition === 'Difficult' ? 'warning' : 'default'}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                  <Chip
                                    label={prosecutor.Prosecutor_Active ? 'Active' : 'Inactive'}
                                    color={prosecutor.Prosecutor_Active ? 'success' : 'error'}
                                    size="small"
                                  />
                                </Box>
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {prosecutor.Prosecutor_Email}
                                </Typography>
                                {prosecutor.Prosecutor_Phone && (
                                  <Typography variant="body2" color="text.secondary">
                                    {prosecutor.Prosecutor_Phone}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                                  ID: {prosecutor.Prosecutor_guid}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        
                        {index < court.prosecutors.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/*
          The docket for this court (Lloyd, 16 Sep 2026). Full width under the two cards, because it
          is a table rather than a detail panel, and it is the part somebody covering the courtroom
          actually came here for.
        */}
        <Box sx={{ mt: 3 }}>
          <CourtClientsPanel Court_guid={court.Court_guid} />
        </Box>

      </Container>

      {/* Prosecutor Edit Modal */}
      <Dialog
        open={prosecutorModalOpen}
        onClose={handleCloseProsecutorModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {showDeleteConfirmation ? 'Confirm Delete' : 'Edit Prosecutor'}
        </DialogTitle>
        
        <DialogContent>
          {showDeleteConfirmation ? (
            <DialogContentText>
              Are you sure you want to remove prosecutor "{selectedProsecutor?.Prosecutor_Name}"? 
              This action will set their status to inactive.
            </DialogContentText>
          ) : (
            <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
              <TextField
                label="Prosecutor Name"
                variant="outlined"
                size="small"
                fullWidth
                value={prosecutorEditForm.Prosecutor_Name}
                onChange={(e) => handleProsecutorFormChange('Prosecutor_Name', e.target.value)}
                disabled={prosecutorModalLoading}
              />
              
              <TextField
                label="Prosecutor Email"
                variant="outlined"
                size="small"
                fullWidth
                type="email"
                value={prosecutorEditForm.Prosecutor_Email}
                onChange={(e) => handleProsecutorFormChange('Prosecutor_Email', e.target.value)}
                disabled={prosecutorModalLoading}
              />

              <TextField
                label="Prosecutor Phone"
                variant="outlined"
                size="small"
                fullWidth
                type="tel"
                value={prosecutorEditForm.Prosecutor_Phone}
                onChange={(e) => handleProsecutorFormChange('Prosecutor_Phone', e.target.value)}
                disabled={prosecutorModalLoading}
                placeholder="Optional phone number"
              />

              <FormControl fullWidth size="small" disabled={prosecutorModalLoading}>
                <InputLabel id="disposition-label">Disposition</InputLabel>
                <Select
                  labelId="disposition-label"
                  label="Disposition"
                  value={prosecutorEditForm.Prosecutor_Disposition}
                  onChange={(e) => handleProsecutorFormChange('Prosecutor_Disposition', e.target.value)}
                >
                  <MenuItem value=""><em>Not specified</em></MenuItem>
                  <MenuItem value="Friendly">Friendly</MenuItem>
                  <MenuItem value="Difficult">Difficult</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={prosecutorEditForm.Prosecutor_Active}
                    onChange={(e) => handleProsecutorFormChange('Prosecutor_Active', e.target.checked)}
                    disabled={prosecutorModalLoading}
                  />
                }
                label="Active"
              />
              
              <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                ID: {selectedProsecutor?.Prosecutor_guid}
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          {showDeleteConfirmation ? (
            <>
              <Button 
                onClick={() => setShowDeleteConfirmation(false)} 
                disabled={prosecutorModalLoading}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteProsecutor} 
                color="error" 
                variant="contained"
                disabled={prosecutorModalLoading}
                startIcon={prosecutorModalLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
              >
                {prosecutorModalLoading ? 'Removing...' : 'Remove'}
              </Button>
            </>
          ) : (
            <>
              <Button 
                onClick={handleCloseProsecutorModal} 
                disabled={prosecutorModalLoading}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => setShowDeleteConfirmation(true)} 
                color="error"
                disabled={prosecutorModalLoading}
                startIcon={<DeleteIcon />}
              >
                Remove
              </Button>
              <Button 
                onClick={handleSaveProsecutor} 
                variant="contained"
                disabled={prosecutorModalLoading}
                startIcon={prosecutorModalLoading ? <CircularProgress size={16} /> : <SaveIcon />}
              >
                {prosecutorModalLoading ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ManagerCourtView;
